import { useState, type ReactNode } from "react"

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  PhotoIcon,
  SunIcon,
} from "@heroicons/react/24/outline"

import {
  Eyebrow,
  GradText,
  Icon,
  Section,
  SectionHeading,
} from "../../BatteryOnlyProposal/primitives"
import type {
  SolarComponent,
  SolarResidence,
  SolarSpec,
  SolarSystem,
} from "../solar-presentation"
import { HomePicture } from "./roof-layout"

export const SITE_IMAGE_UNAVAILABLE = `Site image unavailable`

/**
 * The aerial card. It edits nothing itself; an editor's page passes the
 * design's edit entry point as `action`.
 */
export function Home({
  residence,
  action,
}: {
  residence: SolarResidence
  action?: ReactNode
}): JSX.Element {
  const { address, familyName, image, layout } = residence
  const location = `${address.city}, ${address.state} ${address.postal}`
  // A stored image whose file is gone falls back to the gradient, not a broken image.
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const showsImage = image != null && image.url !== failedUrl

  return (
    <Section id="home">
      <div className="relative h-[360px] overflow-hidden rounded-[24px] border border-bp-border bg-bp-elevated lg:h-[460px]">
        {showsImage ? (
          <HomePicture
            image={image}
            layout={layout}
            onError={() => setFailedUrl(image.url)}
            alt={
              familyName
                ? `Aerial view of the ${familyName} residence`
                : `Aerial view of ${address.street}`
            }
          />
        ) : null}
        <div className="pointer-events-none absolute inset-0 bg-[image:linear-gradient(180deg,transparent_40%,rgba(0,0,0,0.7)_100%)]" />
        {showsImage ? null : (
          <p
            data-testid="solar-site-image-unavailable"
            className="absolute top-6 left-6 inline-flex items-center gap-2 rounded-full border border-bp-border bg-bp-glass-hi px-3 py-1.5 text-[length:var(--step--2)] font-semibold text-bp-text-muted backdrop-blur lg:top-9 lg:left-12"
          >
            <PhotoIcon aria-hidden="true" className="size-4" />
            {SITE_IMAGE_UNAVAILABLE}
          </p>
        )}
        {action ? (
          <div className="absolute top-6 right-6 lg:top-9 lg:right-12">
            {action}
          </div>
        ) : null}
        <div className="absolute right-6 bottom-6 left-6 lg:right-12 lg:bottom-9 lg:left-12">
          {familyName ? (
            <Eyebrow className="mb-2">The {familyName} residence</Eyebrow>
          ) : null}
          <h2 className="font-display mb-2 text-[length:var(--step-3)] font-semibold tracking-[-0.025em] text-bp-text">
            {address.street}
          </h2>
          <div className="text-[length:var(--step--1)] text-bp-text-muted">
            {[location, ...residence.siteDetails].join(` · `)}
          </div>
        </div>
      </div>
    </Section>
  )
}

function SpecIcon({ icon }: { icon: SolarSpec[`icon`] }): JSX.Element {
  return icon === `sun` ? (
    <SunIcon aria-hidden="true" className="size-3.5" />
  ) : (
    <Icon name={icon} className="size-3.5" />
  )
}

export function SystemSpecs({ system }: { system: SolarSystem }): JSX.Element {
  return (
    <Section id="system">
      <SectionHeading eyebrow="The system">
        {system.hasStorage ? (
          <>
            Tier-1 panels, <GradText>smart storage</GradText>, designed for your
            roof.
          </>
        ) : (
          <>
            Tier-1 panels, <GradText>designed for your roof</GradText>.
          </>
        )}
      </SectionHeading>
      <div
        className={`grid grid-cols-2 gap-3.5 md:grid-cols-3 ${system.hasStorage ? `xl:grid-cols-5` : `xl:grid-cols-4`}`}
      >
        {system.specs.map((spec) => (
          <div
            key={spec.label}
            className="relative flex min-h-[168px] flex-col gap-1.5 rounded-lg border border-bp-border bg-bp-surface px-[26px] py-7"
          >
            <div className="absolute top-5 right-[22px] grid size-8 place-items-center rounded-[10px] bg-bp-glass-hi text-bp-accent">
              <SpecIcon icon={spec.icon} />
            </div>
            <Eyebrow>{spec.label}</Eyebrow>
            <div className="font-display mt-1 text-[length:var(--step-3)] font-semibold tracking-[-0.025em] tabular-nums">
              {spec.value}
            </div>
            <div className="mt-auto text-[length:var(--step--2)] text-bp-text-muted">
              {spec.sub}
            </div>
          </div>
        ))}
      </div>
    </Section>
  )
}

function ComponentHeadline({ item }: { item: SolarComponent }): JSX.Element {
  const start = item.highlight ? item.name.indexOf(item.highlight) : -1
  if (start < 0) {
    return <GradText>{item.name}</GradText>
  }
  const end = start + item.highlight.length
  return (
    <>
      {item.name.slice(0, start)}
      <GradText>{item.highlight}</GradText>
      {item.name.slice(end)}
    </>
  )
}

type StatLayout = { columns: number; className: string }

const THREE_STAT_COLUMNS: StatLayout = { columns: 3, className: `grid-cols-3` }

/**
 * Static classes, so Tailwind sees them; a missing stat narrows the row. Four
 * stats (storage that is also the inverter) wrap into two rows of two.
 */
const STAT_COLUMNS: Record<number, StatLayout> = {
  1: { columns: 1, className: `grid-cols-1` },
  2: { columns: 2, className: `grid-cols-2` },
  3: THREE_STAT_COLUMNS,
  4: { columns: 2, className: `grid-cols-2` },
}

/** Dividers between cells only: none on a row's last cell or above the first row. */
const statDivider = (index: number, count: number, columns: number): string =>
  [
    index % columns < columns - 1 && index < count - 1 ? `border-r` : ``,
    index >= columns ? `border-t` : ``,
  ]
    .filter(Boolean)
    .join(` `)

function ComponentSlide({ item }: { item: SolarComponent }): JSX.Element {
  const statLayout = STAT_COLUMNS[item.stats.length] ?? THREE_STAT_COLUMNS
  return (
    <div className="grid items-center gap-10 lg:min-h-[540px] lg:grid-cols-[1.05fr_1fr] lg:gap-[60px]">
      <div className="relative grid h-[320px] place-items-center overflow-hidden lg:h-[520px]">
        <div className="pointer-events-none absolute inset-0 bg-[image:var(--bp-product-glow)]" />
        <img
          src={item.image}
          alt={item.imageAlt}
          loading="lazy"
          decoding="async"
          className="relative block object-contain drop-shadow-[0_30px_40px_rgba(0,0,0,0.55)]"
          style={{
            maxWidth: `${78 * item.imageScale}%`,
            maxHeight: `${82 * item.imageScale}%`,
          }}
        />
      </div>
      <div className="lg:pr-8">
        <Eyebrow className="mb-4 text-bp-accent">{item.kind}</Eyebrow>
        <h3 className="font-display text-[length:var(--step-4)] leading-[1.05] font-semibold tracking-[-0.03em] text-bp-text">
          <ComponentHeadline item={item} />
        </h3>
        <div className="mt-3 text-[length:var(--step--1)] tracking-[0.02em] text-bp-text-muted">
          {item.tag}
        </div>
        {item.summary ? (
          <p className="mt-[22px] max-w-[540px] text-[length:var(--step--1)] leading-relaxed text-bp-text-soft">
            {item.summary}
          </p>
        ) : null}
        {item.stats.length > 0 ? (
          <div
            className={`mt-[26px] grid ${statLayout.className} overflow-hidden rounded-[14px] border border-bp-border bg-bp-elevated`}
          >
            {item.stats.map((stat, index) => (
              <div
                key={stat.label}
                className={`border-bp-border px-[18px] py-4 ${statDivider(index, item.stats.length, statLayout.columns)}`}
              >
                <div className="font-display text-[22px] leading-none font-semibold tracking-[-0.02em] text-bp-text tabular-nums">
                  {stat.value}
                </div>
                <div className="mt-1.5 text-[11px] tracking-[0.04em] text-bp-text-muted">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        ) : null}
        {item.features.length > 0 ? (
          <div className="mt-7 flex flex-col gap-4">
            {item.features.map((feature) => (
              <div
                key={feature.title}
                className="grid grid-cols-[22px_1fr] items-start gap-3.5"
              >
                <div className="mt-[3px] grid size-[18px] place-items-center rounded-full bg-bp-accent-soft text-bp-accent">
                  <Icon name="check" className="size-[11px]" />
                </div>
                <div>
                  <div className="font-display text-[length:var(--step--1)] font-semibold tracking-[-0.01em] text-bp-text">
                    {feature.title}
                  </div>
                  <div className="mt-[3px] max-w-[520px] text-[length:var(--step--2)] leading-relaxed text-bp-text-muted">
                    {feature.body}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

const pad = (value: number): string => String(value).padStart(2, `0`)

/**
 * The component carousel. Tabs, counter and controls cover only the slides
 * given; a single slide needs no tabs or controls.
 */
export function Components({
  components: items,
}: {
  components: SolarComponent[]
}): JSX.Element | null {
  // Selected by id, so a refetch that drops a slide keeps the reader's place.
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const index = Math.max(
    items.findIndex((component) => component.id === selectedId),
    0,
  )
  const item = items[index]
  if (!item) {
    return null
  }
  const hasControls = items.length > 1
  const step = (delta: number): void =>
    setSelectedId(
      items[(index + delta + items.length) % items.length]?.id ?? null,
    )

  return (
    <Section id="components">
      <div className="mb-9 flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow className="mb-3">Engineered components</Eyebrow>
          <h2 className="font-display max-w-[780px] text-[length:var(--step-4)] leading-[1.05] font-semibold tracking-[-0.035em] text-bp-text">
            Every piece is <GradText>tier-one hardware</GradText>, picked for
            your roof.
          </h2>
        </div>
        {hasControls ? (
          <div
            role="tablist"
            aria-label="System components"
            className="flex max-w-full overflow-x-auto rounded-full border border-bp-border bg-bp-surface p-1"
          >
            {items.map((component, componentIndex) => (
              <button
                key={component.id}
                type="button"
                role="tab"
                id={`component-tab-${component.id}`}
                aria-selected={componentIndex === index}
                aria-controls="component-panel"
                onClick={() => setSelectedId(component.id)}
                className={`font-display shrink-0 rounded-full px-3 py-[9px] text-[12.5px] font-semibold whitespace-nowrap transition-colors sm:px-[18px] focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:outline-none ${
                  componentIndex === index
                    ? `bg-bp-accent text-bp-on-accent`
                    : `text-bp-text-muted hover:text-bp-text`
                }`}
              >
                {component.kind}
                <span className="ml-2 text-[11px] tabular-nums opacity-75">
                  {pad(componentIndex + 1)}
                </span>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="relative overflow-hidden rounded-[24px] border border-bp-border bg-bp-surface px-6 py-8 lg:px-12 lg:py-11">
        <div className="pointer-events-none absolute inset-0 bg-[image:radial-gradient(ellipse_50%_70%_at_0%_0%,var(--bp-accent-soft),transparent_50%)]" />
        {/* A lone slide has no tabs, so it is a plain region, not an orphan tabpanel. */}
        <div
          id="component-panel"
          role={hasControls ? `tabpanel` : `region`}
          aria-label={item.kind}
          className="relative"
        >
          <ComponentSlide item={item} />
        </div>
        <div className="relative mt-8 flex items-center justify-between border-t border-bp-border pt-[22px]">
          <div className="flex items-center gap-3.5">
            <span className="font-display text-sm font-semibold text-bp-text tabular-nums">
              {pad(index + 1)}
            </span>
            <div className="relative h-0.5 w-[120px] rounded-sm bg-bp-border">
              <div
                className="absolute inset-y-0 left-0 rounded-sm bg-[image:var(--bp-grad-accent)] transition-[width] duration-300"
                style={{ width: `${((index + 1) / items.length) * 100}%` }}
              />
            </div>
            <span className="text-[13px] text-bp-text-muted tabular-nums">
              {pad(items.length)}
            </span>
          </div>
          {hasControls ? (
            <div className="flex gap-2.5">
              <button
                type="button"
                aria-label="Previous component"
                onClick={() => step(-1)}
                className="grid size-11 place-items-center rounded-full border border-bp-border-strong text-bp-text hover:bg-bp-glass-hi focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:outline-none"
              >
                <ArrowLeftIcon aria-hidden="true" className="size-3.5" />
              </button>
              <button
                type="button"
                aria-label="Next component"
                onClick={() => step(1)}
                className="grid size-11 place-items-center rounded-full bg-bp-accent text-bp-on-accent hover:bg-bp-accent-hover focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:outline-none"
              >
                <ArrowRightIcon aria-hidden="true" className="size-3.5" />
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </Section>
  )
}
